import { SupportedLanguages } from '@core/types/i18n'
import { logError } from '@core/utils/logger'
import type { Dictionary } from '@core/utils/types'
import { type Locale, setDefaultOptions } from 'date-fns'
import { enGB } from 'date-fns/locale'
import type { i18n as I18nInstance } from 'i18next'

// English ships in the bundle; other locales are code-split and fetched on
// demand, mirroring how i18next loads its translation resources.
const dateFnsLocaleLoaders: Dictionary<() => Locale | Promise<Locale>> = {
  [SupportedLanguages.English]: () => enGB,
  [SupportedLanguages.French]: () =>
    import('date-fns/locale/fr').then((m) => m.fr),
}

// Resolved locales, so an already-loaded language re-applies synchronously.
const loadedLocales: Dictionary<Locale> = {
  [SupportedLanguages.English]: enGB,
}

// Bundled default so dates render with a locale before the first load resolves.
setDefaultOptions({ locale: loadedLocales[SupportedLanguages.English] })

function resolveLanguage(lang: string): string {
  const [lng] = lang.split('-')
  return lng && lng in dateFnsLocaleLoaders ? lng : SupportedLanguages.English
}

/**
 * Applies the date-fns locale for a language as the global default. date-fns
 * has no React reactivity, so this only mutates the default — the re-render is
 * owned by i18n.
 *
 * Once a locale is loaded it is cached, so subsequent calls for that language
 * run synchronously (no await). This lets {@link followI18nDateFnsLocale} apply
 * it before React's `languageChanged` re-render reads it.
 *
 * Await it inside i18next's resource loader to pre-warm the cache before the
 * first `languageChanged` for a language, so even the first switch is in sync.
 */
export async function applyDateFnsLocale(lang: string): Promise<void> {
  const lng = resolveLanguage(lang)

  if (!loadedLocales[lng]) {
    const loaded = await dateFnsLocaleLoaders[lng]?.()
    if (loaded) loadedLocales[lng] = loaded
  }

  setDefaultOptions({ locale: loadedLocales[lng] })
}

/**
 * Keeps date-fns' default locale in sync on every language switch — including
 * switches between already-loaded languages, which i18next's resource loader
 * does not re-run. Only follows i18n; it never emits i18n events.
 *
 * For a cached locale the apply is synchronous, so it lands before the
 * `languageChanged` re-render. The first switch to a not-yet-loaded language
 * relies on the resource loader having pre-warmed the cache via
 * {@link applyDateFnsLocale}; without that (e.g. Storybook's static resources)
 * relative dates may update one render late on that first switch only.
 */
export function followI18nDateFnsLocale(i18n: I18nInstance): void {
  i18n.on('languageChanged', (lang) => {
    applyDateFnsLocale(lang).catch(logError)
  })
}
